// Display form of a horse name (horse, sire, dam's sire). Display only: it changes
// letter case and nothing else, so searching with a formatted name finds the same
// horses (name search is case-insensitive).
//
// About a third of names arrive in all capitals ("KAPITANA S", "CHACCO-BLUE").
// Those, and the few all-lowercase ones, are recased following how the data's
// properly cased names write the same words. Names that already mix upper and
// lower case are trusted as-is ("Bob´s Vision xx", "Easy Q").

// Lower case when not the first word: "Darco van de Heffinck", "Lord Pezi xx".
const LOWER_WORDS = new Set([
  'van', "van't", 'de', 'du', 'des', 'het', "'t", 'von', 'af', 'of', 'and', 'den', 'der', 'vd', 'vh', 'xx', 'ox',
]);

const ROMAN_NUMERAL = /^(i{1,3}|iv|vi{0,3}|ix|x)$/;
// Studbook and breeder suffixes are consonant-only abbreviations: VDL, PS, TN, HS, CH.
const ABBREVIATION = /^[b-df-hj-np-tv-xz]{2,4}$/;

const capitalize = (part: string) => part.charAt(0).toUpperCase() + part.slice(1);

const formatWord = (word: string, isFirst: boolean, prevWord: string | null): string => {
  if (!isFirst && LOWER_WORDS.has(word)) return word;
  // "de la", "de le", "du le": "Major de la Croix" (on its own, "La" is usually capitalized)
  if ((word === 'la' || word === 'le') && (prevWord === 'de' || prevWord === 'du')) return word;
  // Country or studbook code in parentheses: "(DNK)", "(OS)"
  if (/^\(\p{L}{1,4}\)$/u.test(word)) return word.toUpperCase();
  if (word.length === 1) return word.toUpperCase(); // "Kapitana S", "Toulon Z"
  if (ROMAN_NUMERAL.test(word) || ABBREVIATION.test(word)) return word.toUpperCase();
  // French elision after the first word: "Bibi d'Emma" (but "D'Argent" on its own)
  if (!isFirst && /^d['’]./.test(word)) return word.slice(0, 2) + formatWord(word.slice(2), true, null);
  // "mckee" → "McKee"
  if (/^mc\p{L}{2,}/u.test(word)) return 'Mc' + capitalize(word.slice(2));
  // Each piece of hyphenated, apostrophe and dotted names: "chacco-blue", "d'argent", "l.f.";
  // a possessive stays lower case: "stendyssen's"
  return word
    .split(/(['’´.-])/)
    .map((part, idx, parts) => (idx > 0 && /['’´]/.test(parts[idx - 1]) && part === 's' ? part : capitalize(part)))
    .join('');
};

export const formatHorseName = (name?: string | null): string | null => {
  if (!name) return null;
  const isSingleCase = name === name.toUpperCase() || name === name.toLowerCase();
  if (!isSingleCase) return name;
  // Split on whitespace but keep it, so the result differs from the input only in case.
  let prevWord: string | null = null;
  return name
    .toLowerCase()
    .split(/(\s+)/)
    .map((token) => {
      if (!token.trim()) return token;
      const formatted = formatWord(token, prevWord === null, prevWord);
      prevWord = token;
      return formatted;
    })
    .join('');
};
